import { Component, Input, OnInit } from '@angular/core';
import { EventModel } from '../shared/event.model';
import { EventService } from '../shared/event.service';
import { CommonModule } from '@angular/common';


interface FinancialBreakdownResponse {
  product_items: {
    item: {
      id: number;
      name: string;
      quantity: number;
      unit_price: number;
      total_amount: number;
      is_venue_hire: boolean;
      is_variant_based: boolean;
      variant_breakdown: {
        base_price: number;
        variants: {
          variant_name: string;
          option_name: string;
          option_price: number;
          quantity: number;
          notes?: string | null;
        }[];
        total_variant_price: number;
      };
    };
    allocations: {
      payment_id: number;
      payment_description: string;
      amount: number;
      percentage: number;
      reason: string;
      reason_description: string;
    }[];
  }[];
  payments: {
    payment: {
      id: number;
      description: string;
      sync_target: string;
      total_amount: number;
      date_due: string;
      amount_paid: number;
      is_reservation_fee: boolean;
    };
    calculation_facts: string[];
    credits_received: {
      from_payment_id: number;
      from_sync_target: string;
      amount: number;
      reason: string;
    }[];
    credits_given: any[];
    overpayment: number;
  }[];
  summary: {
    total_items_value: number;
    total_payments_due: number;
    total_amount_paid: number;
    venue_hire_adjustment: number;
    reservation_fee_credit: number;
    net_payable: number;
  };
}

@Component({
  selector: 'app-financial-breakdown',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="bg-base-100 border border-base-300 font-mono shadow-lg">
      @if (loading) {
        <div class="text-center py-8">
          <div class="loading loading-spinner loading-lg"></div>
          <div class="mt-4">Loading financial breakdown...</div>
        </div>
      } @else if (breakdown) {
        <!-- Bank Statement Header -->
        <div class="border-b border-base-300 p-6 bg-base-200">
          <div class="text-center text-lg font-bold mb-1 text-base-content">
            ───────────────────────────────────────────────
          </div>
          <div class="text-center text-xl font-bold mb-1 text-primary">
            FINANCIAL BREAKDOWN STATEMENT
          </div>
          <div class="text-center text-lg font-bold mb-4 text-base-content">
            ───────────────────────────────────────────────
          </div>
          <div class="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <div class="font-bold text-secondary">EVENT</div>
              <div class="text-base-content">{{ event.name || 'Unknown Event' }}</div>
            </div>
            <div>
              <div class="font-bold text-secondary">REFERENCE</div>
              <div class="text-base-content">{{ event.reference || 'N/A' }}</div>
            </div>
            <div>
              <div class="font-bold text-secondary">STATEMENT DATE</div>
              <div class="text-base-content">{{ getCurrentDate() }}</div>
            </div>
          </div>
        </div>

        <!-- Summary Section -->
        <div class="p-6 border-b border-base-300">
          <div class="text-center font-bold text-base mb-4 text-primary">ACCOUNT SUMMARY</div>
          <div class="border border-base-300 shadow-sm">
            <div class="bg-base-300 text-base-content px-4 py-2 text-xs uppercase">
              <div class="grid grid-cols-2 gap-4 opacity-70 font-medium">
                <div>Description</div>
                <div class="text-right">Amount</div>
              </div>
            </div>
            <div class="px-4 py-2 border-b border-base-300 bg-base-100">
              <div class="grid grid-cols-2 gap-4">
                <div class="text-base-content">Total Product Value</div>
                <div class="text-right font-semibold text-success">{{ formatCurrency(breakdown.summary.total_items_value) }}</div>
              </div>
            </div>
            <div class="px-4 py-2 border-b border-base-300 bg-base-100">
              <div class="grid grid-cols-2 gap-4">
                <div class="text-base-content">Total Payments Due</div>
                <div class="text-right font-semibold text-info">{{ formatCurrency(breakdown.summary.total_payments_due) }}</div>
              </div>
            </div>
            <div class="px-4 py-2 border-b border-base-300 bg-base-100">
              <div class="grid grid-cols-2 gap-4">
                <div class="text-base-content">Total Amount Paid</div>
                <div class="text-right font-semibold text-success">{{ formatCurrency(breakdown.summary.total_amount_paid) }}</div>
              </div>
            </div>
            @if (breakdown.summary.total_payments_due - breakdown.summary.total_amount_paid > 0) {
              <div class="px-4 py-2 bg-warning text-warning-content font-bold border-t border-base-300">
                <div class="grid grid-cols-2 gap-4">
                  <div>Outstanding Balance</div>
                  <div class="text-right">{{ formatCurrency(breakdown.summary.total_payments_due - breakdown.summary.total_amount_paid) }}</div>
                </div>
              </div>
            } @else if (breakdown.summary.total_payments_due - breakdown.summary.total_amount_paid < 0) {
              <div class="px-4 py-2 bg-success text-success-content font-bold border-t border-base-300">
                <div class="grid grid-cols-2 gap-4">
                  <div>Credit (Overpaid)</div>
                  <div class="text-right">{{ formatCurrency(Math.abs(breakdown.summary.total_payments_due - breakdown.summary.total_amount_paid)) }}</div>
                </div>
              </div>
            } @else {
              <div class="px-4 py-2 bg-info text-info-content font-bold border-t border-base-300">
                <div class="grid grid-cols-2 gap-4">
                  <div>Balance</div>
                  <div class="text-right">{{ formatCurrency(0) }}</div>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Payment Breakdown -->
        <div class="p-6 border-b border-base-300">
          <div class="text-center font-bold text-base mb-6 text-primary">PAYMENT ALLOCATION DETAILS</div>
          @for (paymentData of breakdown.payments; track paymentData.payment.id; let i = $index) {
            <div class="mb-8 border border-base-300 shadow-sm card">
              <!-- Payment Header -->
              <div class="bg-base-200 text-base-content px-4 py-4 border-b border-base-300">
                <div class="space-y-3">
                  <div>
                    <div class="text-xs opacity-60 uppercase tracking-wide">Payment {{ i + 1 }}</div>
                    <div class="text-lg font-bold text-primary">{{ getPaymentTitle(paymentData) }}</div>
                  </div>
                  <div>
                    <div class="text-xs opacity-60 uppercase tracking-wide">Due Date</div>
                    <div class="font-semibold">{{ formatDate(paymentData.payment.date_due) || 'N/A' }}</div>
                  </div>
                </div>
              </div>

              @if (paymentData.calculation_facts && paymentData.calculation_facts.length > 0) {
                <div>
                  <!-- Calculation Facts Header -->
                  <div class="bg-base-300 text-base-content px-4 py-2 text-xs uppercase border-b border-base-300">
                    <div class="font-medium opacity-70">Payment Calculation Details</div>
                  </div>

                  <!-- Calculation Facts -->
                  @for (fact of paymentData.calculation_facts; track $index) {
                    <div class="px-4 py-3 text-sm border-b border-base-300 bg-base-100">
                      <div class="text-base-content">{{ fact }}</div>
                    </div>
                  }
                </div>
              } @else {
                <div class="px-4 py-3 text-sm italic text-base-content/60 border-b border-base-300 bg-base-100">
                  No calculation details available for this payment
                </div>
              }

              <!-- Credits Received -->
              @if (paymentData.credits_received && paymentData.credits_received.length > 0) {
                <div>
                  <div class="bg-emerald-100 text-emerald-900 px-4 py-2 text-xs uppercase border-b border-emerald-300">
                    <div class="font-medium">Credits Applied to This Payment</div>
                  </div>
                  @for (credit of paymentData.credits_received; track $index) {
                    <div class="px-4 py-3 text-sm border-b border-base-300 bg-base-100">
                      <div class="grid grid-cols-2 gap-4">
                        <div class="text-base-content">
                          <div class="font-medium text-emerald-700">{{ credit.reason }}</div>
                          <div class="text-xs text-base-content/60 mt-1">From: {{ getPaymentTitleByTarget(credit.from_sync_target) }}</div>
                        </div>
                        <div class="text-right font-semibold text-emerald-700">-{{ formatCurrency(credit.amount) }}</div>
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Credits Given -->
              @if (paymentData.credits_given && paymentData.credits_given.length > 0) {
                <div>
                  <div class="bg-amber-100 text-amber-900 px-4 py-2 text-xs uppercase border-b border-amber-300">
                    <div class="font-medium">Credits Given by This Payment</div>
                  </div>
                  @for (credit of paymentData.credits_given; track $index) {
                    <div class="px-4 py-3 text-sm border-b border-base-300 bg-base-100">
                      <div class="grid grid-cols-2 gap-4">
                        <div class="text-base-content">
                          <div class="font-medium text-amber-700">{{ credit.reason }}</div>
                          <div class="text-xs text-base-content/60 mt-1">To: {{ getPaymentTitleByTarget(credit.to_sync_target) }}</div>
                        </div>
                        <div class="text-right font-semibold text-amber-700">+{{ formatCurrency(credit.amount) }}</div>
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Overpayment Warning -->
              @if (paymentData.overpayment && paymentData.overpayment > 0) {
                <div class="bg-warning text-warning-content px-4 py-3 border-b border-base-300">
                  <div class="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <div class="font-bold">Overpayment Detected</div>
                      <div class="text-sm">This payment has been overpaid by {{ formatCurrency(paymentData.overpayment) }}</div>
                    </div>
                  </div>
                </div>
              }

              <!-- Payment Total -->
              <div class="bg-base-300 text-base-content px-4 py-3 border-t border-base-300">
                <div class="flex justify-end">
                  <div class="text-right">
                    <div class="text-sm font-medium text-base-content/70 mb-1">PAYMENT TOTAL</div>
                    <div class="text-xl font-bold">{{ formatCurrency(paymentData.payment.total_amount) }}</div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Product Allocation Matrix -->
        <div class="p-6 border-b border-base-300">
          <div class="text-center font-bold text-base mb-6 text-primary">PRODUCT ALLOCATION MATRIX</div>
          @for (productData of breakdown.product_items; track productData.item.id) {
            <div class="mb-6 border border-base-300 shadow-sm card">
              <!-- Product Header -->
              <div class="bg-base-200 text-base-content px-4 py-3 border-b border-base-300">
                <div class="grid grid-cols-3 gap-4">
                  <div>
                    <div class="text-xs opacity-60 uppercase">Product</div>
                    <div class="font-semibold">{{ productData.item.name }}</div>
                    @if (productData.item.variant_breakdown.variants.length > 0) {
                      <div class="mt-2 space-y-1">
                        @for (variant of productData.item.variant_breakdown.variants; track $index) {
                          <div class="text-xs text-base-content/70">
                            <span class="font-medium">{{ variant.variant_name }}:</span> {{ variant.option_name }}
                            @if (variant.option_price !== 0 && variant.quantity > 0) {
                              <span [class]="variant.option_price > 0 ? 'text-success' : 'text-error'">
                                {{ variant.option_price > 0 ? '+' : '' }}{{ formatCurrency(variant.option_price) }} × {{ variant.quantity }}
                                @if (variant.quantity > 1) {
                                  = {{ formatCurrency(variant.option_price * variant.quantity) }}
                                }
                              </span>
                            } @else if (variant.quantity > 0 && variant.option_price === 0) {
                              <span class="text-base-content/60">
                                £0 × {{ variant.quantity }}
                                @if (variant.quantity > 1) {
                                  = £0
                                }
                              </span>
                            }
                            @if (variant.notes) {
                              <div class="text-xs italic text-base-content/50 mt-1">{{ variant.notes }}</div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                  <div>
                    @if (!productData.item.is_variant_based) {
                      <div class="text-xs opacity-60 uppercase">Quantity</div>
                      <div class="font-semibold">{{ productData.item.quantity }}x</div>
                      @if (productData.item.variant_breakdown.base_price > 0) {
                        <div class="text-xs text-base-content/70 mt-1">
                          Base: {{ formatCurrency(productData.item.variant_breakdown.base_price) }}
                        </div>
                      }
                      @if (productData.item.variant_breakdown.total_variant_price > 0) {
                        <div class="text-xs text-base-content/70">
                          Variants: {{ formatCurrency(productData.item.variant_breakdown.total_variant_price) }}
                        </div>
                      }
                    } @else {
                      <div class="text-xs opacity-60 uppercase">Breakdown</div>
                      @if (productData.item.variant_breakdown.total_variant_price > 0) {
                        <div class="text-xs text-base-content/70">
                          Total: {{ formatCurrency(productData.item.variant_breakdown.total_variant_price) }}
                        </div>
                      }
                    }
                  </div>
                  <div class="text-right">
                    <div class="text-xs opacity-60 uppercase">Total Value</div>
                    <div class="text-lg font-bold text-primary">{{ formatCurrency(productData.item.total_amount) }}</div>
                    @if (!productData.item.is_variant_based && (productData.item.variant_breakdown.base_price > 0 || productData.item.variant_breakdown.total_variant_price > 0)) {
                      <div class="text-xs text-base-content/60 mt-1">
                        {{ formatCurrency((productData.item.variant_breakdown.base_price + productData.item.variant_breakdown.total_variant_price) * productData.item.quantity) }}
                      </div>
                    }
                  </div>
                </div>
              </div>

              @if (productData.allocations.length > 0) {
                <div>
                  <!-- Allocation Header -->
                  <div class="bg-base-300 text-base-content px-4 py-2 text-xs uppercase border-b border-base-300">
                    <div class="grid grid-cols-12 gap-2 opacity-70 font-medium">
                      <div class="col-span-4">Allocated to Payment</div>
                      <div class="col-span-4">Allocation Reason</div>
                      <div class="col-span-2">Percentage</div>
                      <div class="col-span-2 text-right">Amount</div>
                    </div>
                  </div>

                  <!-- Allocations -->
                  @for (allocation of productData.allocations; track allocation.payment_id) {
                    <div class="px-4 py-3 text-sm border-b border-base-300 bg-base-100">
                      <div class="grid grid-cols-12 gap-2">
                        <div class="col-span-4 font-medium text-base-content">{{ allocation.payment_description }}</div>
                        <div class="col-span-4 text-base-content">{{ allocation.reason_description }}</div>
                        <div class="col-span-2 text-accent">{{ allocation.percentage | number:'1.0-0' }}%</div>
                        <div class="col-span-2 text-right font-semibold text-success">{{ formatCurrency(allocation.amount) }}</div>
                      </div>
                    </div>
                  }

                  <!-- Product Total -->
                  <div class="bg-base-300 text-base-content px-4 py-3">
                    <div class="flex justify-end">
                      <div class="text-right">
                        <div class="text-xs font-medium text-base-content/70 mb-1">ALLOCATED TOTAL</div>
                        <div class="text-lg font-bold">{{ formatCurrency(getTotalAllocatedForProduct(productData)) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              } @else {
                <div class="px-4 py-3 text-sm italic text-base-content/60 bg-base-100">
                  No allocations found for this product
                </div>
              }
            </div>
          }
        </div>

        <!-- Footer -->
        <div class="p-6 text-center bg-base-200">
          <div class="text-lg font-bold mb-1 text-base-content">
            ───────────────────────────────────────────────
          </div>
          <div class="text-base font-bold mb-2 text-primary">END OF STATEMENT</div>
          <div class="text-sm mb-2 text-base-content">Generated: {{ getCurrentDateTime() }}</div>
          <div class="text-xs text-base-content/60 mb-2">
            This statement provides a complete breakdown of payment allocations<br>
            with transparent reasoning for all financial transactions.
          </div>
          <div class="text-lg font-bold text-base-content">
            ───────────────────────────────────────────────
          </div>
        </div>

      } @else if (error) {
        <div class="text-center py-4">
          <div class="alert alert-error">
            <span>ERROR: {{ error }}</span>
          </div>
        </div>
      }
    </div>
  `
})
export class FinancialBreakdownComponent implements OnInit {
  @Input() event!: EventModel;

  breakdown: FinancialBreakdownResponse | null = null;
  loading = false;
  error: string | null = null;

  // Expose Math for template use
  Math = Math;

  constructor(private eventService: EventService) {}

  ngOnInit() {
    this.loadFinancialBreakdown();
  }

  private loadFinancialBreakdown() {
    this.loading = true;
    this.error = null;

    this.eventService.getFinancialBreakdown(this.event.id).subscribe({
      next: (data) => {
        this.breakdown = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.message || 'Failed to load financial breakdown';
        this.loading = false;
        console.error('Error loading financial breakdown:', err);
      }
    });
  }

  getPaymentTitle(paymentData: any): string {
    if (paymentData.payment.is_reservation_fee) {
      return 'Reservation Fee';
    }

    switch (paymentData.payment.sync_target) {
      case 'food_drink_50':
        return 'Second Payment';
      case 'final_payment_plus_deposit':
        return 'Final Payment + Deposit';
      case 'venue_hire':
        return 'First Payment';
      default:
        return paymentData.payment.description || 'Manual Payment';
    }
  }

  getPaymentTitleByTarget(syncTarget: string): string {
    switch (syncTarget) {
      case 'food_drink_50':
        return 'Second Payment';
      case 'final_payment_plus_deposit':
        return 'Final Payment + Deposit';
      case 'venue_hire':
        return 'First Payment';
      default:
        return 'Manual Payment';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  getCurrentDateTime(): string {
    return new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  getTotalAllocatedForProduct(productData: any): number {
    return productData.allocations.reduce((total: number, allocation: any) => {
      return total + (allocation.amount || 0);
    }, 0);
  }
}
